import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@framework/ui/atoms/Button';
import { Loading } from '@framework/ui/atoms/Loading';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { useGroupMemberUserIds } from '@group/hooks';
import type { WorkspaceEntity, WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import { MemberAddList } from './MemberAddList';
import { RoleSelect } from './RoleSelect';
import { classNames } from '@framework/utils';
import { difference } from 'lodash-es';
import { trpcClient } from '@framework/trpcClient';
import { useMemberFilter } from './useMemberFilter';
import type { GroupMemberRolesJSON } from 'balus-common';

type Props = {
  isOpen: boolean;
  onClose(): void;
  workspace: WorkspaceEntity;
  memberRoles: WorkspaceMemberRoles;
  groupMemberRoles: GroupMemberRolesJSON;
};

export const MemberAddModal: React.FC<Props> = ({
  isOpen,
  onClose,
  workspace,
  memberRoles,
  groupMemberRoles,
}: Props) => {
  const { t } = useTranslation();
  const groupMemberUserIds = useGroupMemberUserIds(workspace.groupId);
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [role, setRole] = useState<WorkspaceMemberRole>(WorkspaceMemberRole.admin);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [MemberSearchPanel, searchPanelProps, memberFilter, resetMemberFilter] = useMemberFilter();

  const changed = selectedUserIds.length > 0;

  const handleChangeUserIds = useCallback((userIds: string[]) => setSelectedUserIds(userIds), []);
  const handleChangeRole = useCallback((role: WorkspaceMemberRole) => setRole(role), []);

  const handleClose = useCallback(() => {
    setProcessing(false);
    setErrorMessage('');
    setSelectedUserIds([]);
    setRole(WorkspaceMemberRole.admin);
    setIsSelectedAll(false);
    resetMemberFilter();

    onClose();
  }, [onClose, resetMemberFilter]);

  const handleSubmit = useCallback(async () => {
    setProcessing(true);
    setErrorMessage('');

    try {
      await trpcClient.workspace.addMembers.mutate({ workspaceId: workspace.id, userIds: selectedUserIds, role });
      handleClose();
    } catch (e) {
      setErrorMessage(t('workspace.memberAdd.error'));
      console.error(e);
      setProcessing(false);
    }
  }, [workspace.id, selectedUserIds, role, handleClose, t]);

  const submitButton = (
    <Button color="primary" onClick={handleSubmit} disabled={!changed || processing} loading={processing}>
      {t('common.action.add')}
    </Button>
  );

  const cancelButton = (
    <Button color="secondary" onClick={handleClose} disabled={processing}>
      {t('common.action.cancel')}
    </Button>
  );

  const handleChangeSelectedAll = () => {
    if (!groupMemberUserIds) {
      return;
    }
    const workspaceUserIds = memberRoles.userIds();
    const availableUserIds = difference(groupMemberUserIds, workspaceUserIds);

    setSelectedUserIds(isSelectedAll ? [] : availableUserIds);
    setIsSelectedAll(!isSelectedAll);
  };

  const hasGuest = Object.entries(groupMemberRoles)
    .filter(([userId]) => selectedUserIds.includes(userId))
    .some(([, role]) => role === 'guest');

  useEffect(() => {
    // ゲストが選択された時に、ロールの選択肢が admin の場合には editor に変更する
    if (hasGuest) {
      setRole((role) => (role === WorkspaceMemberRole.admin ? WorkspaceMemberRole.editor : role));
    }
  }, [hasGuest]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.memberAdd.title', { name: workspace.name.value })}
      submitButton={submitButton}
      cancelButton={cancelButton}
    >
      <div className="mb-4 mt-2 p-2" style={{ width: '600px' }}>
        {groupMemberUserIds ? (
          <>
            <label className="mb-5 flex grow cursor-pointer flex-row px-5">
              <input
                type="checkbox"
                className={classNames(
                  'form-checkbox mr-1 h-6 w-6 border-brand',
                  processing ? 'cursor-not-allowed bg-gray-300' : 'cursor-pointer'
                )}
                onChange={handleChangeSelectedAll}
                checked={isSelectedAll}
                disabled={processing}
              />
              <span className="flex h-6 grow truncate px-2 leading-6">{t('workspace.memberAdd.selectAll')}</span>
            </label>
            <MemberSearchPanel {...searchPanelProps} />
            <div className="my-2 overflow-y-auto" style={{ minHeight: '200px', maxHeight: '50vh' }}>
              <MemberAddList
                groupId={workspace.groupId}
                workspaceId={workspace.id}
                groupMemberRoles={groupMemberRoles}
                selectedUserIds={selectedUserIds}
                disabled={processing}
                onChange={handleChangeUserIds}
                memberFilter={memberFilter}
              />
            </div>
            {selectedUserIds.length > 0 ? (
              <div className="py-2 pl-10">
                <Trans
                  i18nKey="workspace.memberAdd.roleAssign"
                  values={{ count: selectedUserIds.length }}
                  components={{
                    roleSelect: (
                      <RoleSelect role={role} onChange={handleChangeRole} disabled={processing} isGuest={hasGuest} />
                    ),
                  }}
                />
                {hasGuest && (
                  <>
                    <br />
                    <span className="text-sm">{t('workspace.memberAdd.guestRoleNote')}</span>
                  </>
                )}
              </div>
            ) : (
              <div className="h-12 py-2"></div>
            )}
          </>
        ) : (
          <Loading />
        )}
      </div>
      {errorMessage && <div className="p-2 text-red-700">{errorMessage}</div>}
    </GeneralModal>
  );
};
